export type ChatRole = 'user' | 'assistant' | 'system';

const ROLE_SORT_ORDER: Record<ChatRole, number> = {
  user: 0,
  assistant: 1,
  system: 2,
};

/**
 * 메시지를 생성 시각 기준 오름차순으로 정렬한다.
 * 동일 시각이면 user → assistant 순으로 정렬한다.
 */
export function sortChatMessages(messages: ChatMessage[]): ChatMessage[] {
  return [...messages].sort((a, b) => {
    const timeDiff = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
    if (timeDiff !== 0) {
      return timeDiff;
    }
    return ROLE_SORT_ORDER[a.role] - ROLE_SORT_ORDER[b.role];
  });
}

/**
 * 서버 메시지와 로컬 임시 사용자 메시지를 병합한다.
 * 아직 서버에 반영되지 않은 temp-user 메시지를 유지한다.
 */
export function mergeServerMessages(
  serverMessages: ChatMessage[],
  localMessages: ChatMessage[],
): ChatMessage[] {
  const sortedServer = sortChatMessages(serverMessages);
  const pendingUsers = localMessages.filter((message) => {
    if (!message.messageId.startsWith('temp-user-')) {
      return false;
    }
    const existsOnServer = sortedServer.some(
      (serverMessage) => serverMessage.role === 'user' && serverMessage.content === message.content,
    );
    return !existsOnServer;
  });
  return sortChatMessages([...sortedServer, ...pendingUsers]);
}

export interface ChatSession {
  sessionId: string;
  title: string;
  summary?: string;
  createdAt: string;
  updatedAt: string;
}

export interface ChatMessage {
  messageId: string;
  sessionId: string;
  role: ChatRole;
  content: string;
  createdAt: string;
  imageUrl?: string;
  inventorySuggestion?: InventorySuggestion;
}

/**
 * AI가 분석한 영수증 기반 인벤토리 등록 제안.
 * emarket-carbon POST /ai/inventory 요청 DTO와 동일한 구조.
 */
export interface InventorySuggestion {
  year: string;
  month: string;
  scopeId: string;
  scopeName: string;
  emsnCoefType: string;
  emsnCoefTypeName: string;
  activityQty: number;
  unit: string;
  confidence: number;
  vendorName?: string;
  /** Scope 3(scopId=9) 전용: 교통수단 종류 코드 (TRNKND*) */
  emsnTrnsmnType?: string;
  emsnTrnsmnTypeName?: string;
  /** 등록 대상 배출시설 조직 ID */
  orgId?: string;
  /** 등록 대상 배출시설명 */
  emsnFcltNm?: string;
}

export interface ChatStreamChunk {
  token?: string;
  status?: 'rag' | 'data';
  done?: boolean;
  sessionId?: string;
  error?: string;
  inventory_suggestion?: InventorySuggestion;
  clarification?: string;
}
